import { ChangeDetectionStrategy, Component, Input, computed, signal } from '@angular/core';
import { IWorkspace, PaymentService } from '../services/payment.service';

/**
 * Workspace identity shown at the right edge of the Workspace Overview hero.
 *
 * Interactivity is derived from the authoritative workspace membership list on the
 * subscription response: a single workspace renders as plain text with no selector
 * affordance. Switching the active workspace has no supported backend action yet,
 * so the expanded panel lists membership only.
 */
@Component({
  selector: 'app-workspace-identity',
  templateUrl: './workspace-identity.component.html',
  styleUrls: ['./workspace-identity.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class WorkspaceIdentityComponent {
  private readonly activeWorkspaceId = signal<string | null>(null);

  /** Workspace id returned by `/api/context`; selects the active row when present. */
  @Input() set activeId(value: string | null) {
    this.activeWorkspaceId.set(value ?? null);
  }

  public readonly open = signal(false);

  public readonly workspaces = computed<IWorkspace[]>(() => this.paymentService.workspace() || []);

  public readonly active = computed<IWorkspace | null>(() => {
    const list = this.workspaces();
    const id = this.activeWorkspaceId();
    return (id ? list.find((workspace) => workspace?.customer === id) : null) || list[0] || null;
  });

  /** Only render a selector affordance when the user belongs to more than one workspace. */
  public readonly interactive = computed(() => this.workspaces().length > 1);

  public readonly name = computed(() => this.active()?.title || 'Your workspace');

  constructor(private paymentService: PaymentService) {}

  public toggle(): void {
    if (!this.interactive()) {
      return;
    }
    this.open.set(!this.open());
  }

  public isActive(workspace: IWorkspace): boolean {
    return workspace === this.active();
  }
}
